import { deviceGet } from "api/device";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { setLoading } from "store/appSlice";
import General from "./components/general";
import { Device } from "api/types/device";
import Installer from "./components/installer";
import Configuration from "./components/configuration";
import Usage from "./components/usage";
import {
  DeviceConfiguration,
  deviceConfiguration,
} from "models/devices/configuration";
import Timeseries from "./components/timeseries";
import UsageMulti from "./components/usage-multi";
import MultiLine from "./components/multi-line";
import GatewayOverview from "./components/gateway";
import useViewportDimensions, {
  SecondaryHeightLayout,
} from "hooks/viewport-dimensions";
import { notifySimple } from "providers/toast";
import { useTranslation } from "react-i18next";

const Devices = () => {
  const { dimensions, getControlHeight } = useViewportDimensions();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  // Extract the filter parameter
  const snr = searchParams.get("snr");

  const [device, setDevice] = useState<Device>(null);
  const [config, setConfig] = useState<DeviceConfiguration>(null);
  const [refresh, setRefresh] = useState(0);

  const fetchData = async () => {
    dispatch(setLoading(true));
    const { data, status, isLoading, error } = await deviceGet(snr);
    dispatch(setLoading(isLoading));
    if (!error && status && status === 200) {
      const config = deviceConfiguration.getDeviceByTag(data.model);
      setConfig(config);
      setDevice(data);

      // Check if device timezone differs from local timezone
      if (data.timezone !== undefined && data.timezone !== null) {
        const localTimezoneOffset = new Date().getTimezoneOffset();
        const deviceTimezoneOffset = data.timezone * 60;
        if (deviceTimezoneOffset !== Math.abs(localTimezoneOffset)) {
          notifySimple(t("toast.timezone.mismatch"), "warning");
        }
      }

      setTimeout(() => setRefresh((r) => r + 1), 100);
    }
  };

  useEffect(() => {
    fetchData();
  }, [snr]);

  const cardHeight = useCallback(() => {
    const calculatedHeight =
      dimensions.contentHeight - getControlHeight("see") - 16;
    const minHeight = 400; // Minimum height in pixels
    return Math.max(calculatedHeight, minHeight);
  }, [refresh]);

  return (
    <>
      {config && (
        <SecondaryHeightLayout>
          <div className="flex flex-col gap-4">
            <div id="see" className="flex flex-col gap-4 md:flex-row">
              <div className="lg:w-1/3">
                <General device={device} onUpdate={fetchData} config={config} />
              </div>

              <div className="flex w-full flex-col gap-4 lg:w-2/3 lg:flex-row">
                <div className="lg:w-1/3">
                  <Installer device={device} config={config} />
                </div>

                <div className="lg:w-2/3">
                  <Configuration
                    device={device}
                    config={config}
                    onUpdate={fetchData}
                  />
                </div>
              </div>
            </div>

            <div
              className={config.type === "gateway" ? "w-full" : "h-full"}
              key={`chart-area-${refresh}`}
              style={
                config.type === "gateway"
                  ? undefined
                  : { height: cardHeight() }
              }
            >
              {config.type === "multi-line" && (
                <MultiLine
                  device={device}
                  config={config}
                  height={cardHeight()}
                />
              )}
              {config.type === "usage" && (
                <Usage device={device} config={config} height={cardHeight()} />
              )}
              {config.type === "usage-multi" && (
                <UsageMulti
                  device={device}
                  config={config}
                  height={cardHeight()}
                />
              )}
              {config.type === "average" && (
                <Timeseries
                  device={device}
                  config={config}
                  height={cardHeight()}
                />
              )}
              {config.type === "gateway" && (
                <GatewayOverview device={device} config={config} />
              )}
            </div>
          </div>
        </SecondaryHeightLayout>
      )}
    </>
  );
};

export default Devices;
